import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { InputGroup, Tooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";
import { buildMissPunchPairUpdate, findContinuousMissPunchSibling, getTimeSheetTimeZone, getUnresolvedMissPunchSide, isAddedTimeSheet, isTimeInputDisabled } from "../helpers";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { useAddedPayCode } from "../hooks/useAddedPayCode";
import { getClockInputStyle, ExceptionTooltip, canTimeSheetBeBinded } from "./useInColumn";
import { MissPunchTooltip } from "./MissPunchTooltip";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { getMandatoryBreak, isMandatoryBreakViolated } from "../../MandatoryBreakWarning/helpers";

export const useOutColumn = ({
    user,
    canEdit,
    clockExceptions,
    view,
    openInOutDateModal,
    openMissPunchModal,
    openSyncTimesheetModal,
    isMyTimesheet,
    openMandatoryBreakWarning,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef();
    const timeFormat = useCompanyTimeFormat();
    const payCode = useAddedPayCode(user);
    const getInputStyle = useMemo(() => {
        if (timeFormat) {
            switch (timeFormat.length) {
                case 1:
                case 2:
                case 3:
                case 4:
                case 5:
                    return { width: '60px' };
                case 6:
                case 7:
                    return { width: '70px' };
                case 8:
                case 9:
                case 10:
                    return { width: '80px' };
                default:
                    return { width: '116px' }
            }
        }
        return { width: '116px' }
    }, [timeFormat])

    return useMemo(() => ({
        Header: t("out"),
        accessor: "endDate",
        alwaysVisible: true,
        headCustomProps: { style: getInputStyle },
        Cell: ({ value, row, cell, updateAddedTimeSheet, updateTimeSheet, data }) => {
            const { original, isNonProductive } = row;
            const disabled = isTimeInputDisabled(original) || row.disabled || cell.disabled || isNonProductive;
            const timeZone = getTimeSheetTimeZone(original);
            const currentValue = value
                ? timeZone
                    ? moment.parseZone(value).toISOString(true)
                    : value
                : undefined;

            const style = getClockInputStyle({
                color: "#ff6161",
                backgroundColor: "#ff61611a",
                border: `1px solid transparent`,
                value,
                original,
            });

            // a row whose *start* is the unknown side has a real clock-out here, so it is not marked
            const missPunchSide = getUnresolvedMissPunchSide(original);
            const isMissingOut = missPunchSide === "end";

            let exceptions = clockExceptions?.filter((exception) => moment(original?.date, "YYYY-MM-DD").isSame(moment(exception?.date, "YYYY-MM-DD"), "date"));
            exceptions = exceptions?.map((exception) => exception?.data);
            exceptions = (exceptions || []).flat();
            exceptions = exceptions?.filter((exception) =>
            (
                [
                    "left_early",
                    "left_late"
                ].includes(exception?.type) &&
                exception?.timesheet?.id === original?.id
            )
            );

            if (openInOutDateModal && original?.endDate) {
                style.borderLeft = 0;
            }

            return (
                <div className="d-flex align-items-center">
                    {isNonProductive || ["unit", "amount"].includes(original?.payCode?.hourType) ? "-" :
                        <InputGroup className="custom-time-input-group">
                            {(openInOutDateModal && original?.endDate && canEdit) && (
                                <span
                                    className={classNames("clock-icon text-center px-1", value && "filled",
                                        original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                    style={{
                                        ...style,
                                        padding: "0.1rem 0.4rem",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        className="cursor-pointer"
                                        icon={faClock}
                                        onClick={() => openInOutDateModal({
                                            column: "out",
                                            user,
                                            original,
                                            updateAddedTimeSheet,
                                            updateTimeSheet
                                        })}
                                        style={{ fontSize: "1rem", paddingTop: "0.2rem" }}
                                    />
                                </span>
                            )}
                            <CustomTimeInput
                                ref={inputRef}
                                timeFormat={timeFormat}
                                className={classNames("time-input text-center", value && "filled", (value && canEdit) && "input-with-value",
                                    isMissingOut && "miss-punch-input",
                                    original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                style={style}
                                disabled={disabled || !canEdit || original?.readOnly}
                                placeholder={["unit", "amount"].includes(original?.payCode?.hourType) ? "-- : --" : t("time")}
                                value={currentValue}
                                onChange={(val) => {
                                    let endDate = "";
                                    if (val) {
                                        endDate = combineDateAndTime(
                                            moment(original.date, "YYYY-MM-DD"),
                                            moment(val),
                                        );
                                        endDate = applyTimezone(endDate, timeZone, true);
                                        if (original.startDate && endDate.isBefore(original.startDate)) {
                                            endDate.add(1, "day");
                                        }

                                        // update sibling
                                        // ?? maybe on binded field change, open a modal to confirm the binding change
                                        // ?? should bind continue if the change causes a cut / conflict / other issues
                                        // TODO: bind should stop if user unbinded the timesheets
                                        if (original.endDate && canTimeSheetBeBinded(original) && openSyncTimesheetModal) {
                                            const sibling = data.find((item) => {
                                                if (!item.startDate || !canTimeSheetBeBinded(item)) {
                                                    return false;
                                                }
                                                if (item.id === original.id) {
                                                    return false;
                                                }
                                                return moment.parseZone(item.startDate)
                                                    .isSame(moment.parseZone(original.endDate), "minute");
                                            });
                                            // TODO: detect swallous / conflicting timesheets / other issues due to change / binding
                                            if (sibling) {
                                                const startDate = endDate.toISOString(true);
                                                // end date can not be used inside the callback, because its value changes asynchronously
                                                const onSuccess = () => {
                                                    const callback = isAddedTimeSheet(sibling)
                                                        ? updateAddedTimeSheet.bind(null, sibling)
                                                        : updateTimeSheet.bind(null, sibling.id)
                                                    callback({ startDate });
                                                }
                                                openSyncTimesheetModal({
                                                    field: t("start-date").toLowerCase(),
                                                    onSuccess,
                                                })
                                            }
                                        }
                                        endDate = endDate.toISOString(true);
                                    }

                                    const duration = original?.startDate && endDate
                                        ? moment(endDate).diff(moment(original?.startDate), "seconds")
                                        : original?.duration || 0;

                                    const updatedFields = { endDate, duration };

                                    const addedTimesheet = isAddedTimeSheet(original);
                                    if (addedTimesheet && !original?.payCode) {
                                        updatedFields.payCode = payCode;
                                    }
                                    const changeValue = (data) => {
                                        if (!val) {
                                            return;
                                        }
                                        // the other half of a continuous miss-punch pair shares this
                                        // unknown moment - see findContinuousMissPunchSibling
                                        const missPunchPair = endDate
                                            ? findContinuousMissPunchSibling(original, data)
                                            : undefined;
                                        const pairFields = buildMissPunchPairUpdate(missPunchPair, endDate);
                                        if (pairFields) {
                                            const pairCallback = isAddedTimeSheet(missPunchPair)
                                                ? updateAddedTimeSheet.bind(null, missPunchPair)
                                                : updateTimeSheet.bind(null, missPunchPair.id);
                                            pairCallback(pairFields);
                                        }
                                        if (isAddedTimeSheet(original)) {
                                            updateAddedTimeSheet(original, updatedFields);
                                        } else {
                                            updateTimeSheet(original.id, updatedFields);
                                        }
                                    }

                                    const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null
                                    if (mandatoryBreak) {
                                        const inViolation = isMandatoryBreakViolated(
                                            {
                                                ...original,
                                                ...updatedFields,
                                                payCode: original.payCode || updatedFields.payCode,
                                            },
                                            mandatoryBreak,
                                        );
                                        if (inViolation) {
                                            openMandatoryBreakWarning({
                                                column: "out",
                                                requiredTime: mandatoryBreak.duration,
                                                original,
                                                updatedFields,
                                                onConfirm: () => {
                                                    changeValue(updatedFields);
                                                },
                                                onCancel: () => {
                                                    // WORKAROUND FOR TIME INPUT
                                                    inputRef.current?.reset();
                                                },
                                            });
                                        } else {
                                            changeValue(updatedFields);
                                        }
                                    } else {
                                        changeValue(updatedFields);
                                    }
                                }}
                            />
                        </InputGroup>
                    }

                    {(exceptions?.length > 0 && view) ? (
                        <ExceptionTooltip
                            original={original}
                            view={view}
                            exceptions={exceptions}
                            style={{
                                color: "#ff6161"
                            }}
                        />
                    ) : null}

                    {(isMissingOut && openMissPunchModal) && (
                        <MissPunchTooltip
                            openMissPunchModal={openMissPunchModal}
                            original={original}
                            isMyTimesheet={isMyTimesheet}
                            side="end"
                        />
                    )}
                </div >
            );
        },
    }), [
        t,
        getInputStyle,
        clockExceptions,
        openInOutDateModal,
        timeFormat,
        canEdit,
        view,
        user,
        payCode,
        isMyTimesheet,
        openSyncTimesheetModal,
        openMissPunchModal,
        openMandatoryBreakWarning,
    ]);
};
